import React, { useEffect, useState, forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import 'react-date-range/dist/styles.css' // main css file
import 'react-date-range/dist/theme/default.css' // theme css file
import { DateRangePicker } from 'react-date-range'
import { addDays } from 'date-fns'
import moment from 'moment'
import writeXlsxFile from 'write-excel-file'

import Input from '../components/Input'
import Button from '../components/Button'
import Select from '../components/Select'
import Loading from '../components/Loading'
import { getParkings } from '../actions/parkingActions'
import {
  downloadValet,
  focValet,
  getCheckinsValet,
  manualCheckoutsValet,
} from '../actions/checkinActions'
import { GET_PARKINGS_RESET } from '../constants/parkingConstants'
import {
  DOWNLOAD_RESET,
  FOC_RESET,
  GET_CHECKINS_RESET,
  MANUAL_CHECKOUT_RESET,
} from '../constants/checkinConstants'

const Checkins = ({ history }) => {
  // * States
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [totalAmount, setTotalAmount] = useState(0)
  const [prepaidAmount, setPrepaidAmount] = useState(0)
  const [postpaidAmount, setPostpaidAmount] = useState(0)
  const [onlineAmount, setOnlineAmount] = useState(0)
  const [inVehicles, setInVehicles] = useState(0)
  const [outVehicles, setOutVehicles] = useState(0)
  const [selected, setSelected] = useState([])
  const [token, setToken] = useState('')

  // * Status
  const [status, setStatus] = useState('')

  // * Parkings
  const [parkingsFormatted, setParkingsFormatted] = useState([])
  const [parking, setParking] = useState('')

  // * Dates
  const [dateModal, setDateModal] = useState(false)
  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 0),
      key: 'selection',
    },
  ])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [dateRange, setDateRange] = useState(false)

  useEffect(() => {
    let dateObject = state[0]
    let { startDate, endDate } = dateObject

    startDate = moment(startDate).format('YYYY-MM-DD')
    endDate = moment(endDate).format('YYYY-MM-DD')

    setFrom(startDate)
    setTo(endDate)
  }, [state])

  const submitDateRange = () => {
    setDateRange(true)
    setDateModal(false)
  }

  // * Vehicle Number
  const [vehicleNumber, setVehicleNumber] = useState('')

  // * Mobile
  const [mobile, setMobile] = useState('')

  // * Table
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)
  const [total, setTotal] = useState(0)

  const [filteredData, setFilteredData] = useState([])
  const [order, setOrder] = useState('ASC')

  // * Table sorting
  const sorting = (col) => {
    if (order === 'ASC') {
      const sorted = [...filteredData].sort((a, b) =>
        a[col].toLowerCase() > b[col].toLowerCase() ? 1 : -1
      )
      setFilteredData(sorted)
      setOrder('DSC')
    }
    if (order === 'DSC') {
      const sorted = [...filteredData].sort((a, b) =>
        a[col].toLowerCase() < b[col].toLowerCase() ? 1 : -1
      )
      setFilteredData(sorted)
      setOrder('ASC')
    }
  }

  // * Initialization
  const dispatch = useDispatch()

  // * Check for auth
  const adminLogin = useSelector((state) => state.adminLogin)
  const { adminInfo } = adminLogin

  useEffect(() => {
    // * Check if user info exists
    if (!adminInfo) {
      history.push('/')
    }
    setToken(adminInfo.token)
  }, [adminInfo, history])

  useEffect(() => {
    setLoading(true)
    dispatch(getParkings())
    dispatch(
      getCheckinsValet(page, status, from, to, parking, vehicleNumber, mobile)
    )
  }, [page])

  // * Get Parkings
  const getParkingsInfo = useSelector((state) => state.getParkingsInfo)
  const { errorGetParkings, getParkingsData } = getParkingsInfo

  useEffect(() => {
    dispatch({ type: GET_PARKINGS_RESET })
    if (getParkingsData) {
      setLoading(false)
      setParkingsFormatted(getParkingsData)
    } else if (errorGetParkings) {
      setLoading(false)
      toast(errorGetParkings, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getParkingsData, errorGetParkings])

  // * Checkins
  const getCheckinsInfo = useSelector((state) => state.getCheckinsInfo)
  const { errorGetCheckins, getCheckinsData } = getCheckinsInfo

  useEffect(() => {
    dispatch({ type: GET_CHECKINS_RESET })
    if (getCheckinsData) {
      setLoading(false)
      setData(getCheckinsData.checkins)
      setPage(getCheckinsData.page)
      setPages(getCheckinsData.pages)
      setTotal(getCheckinsData.count)
      setTotalAmount(getCheckinsData.totalSum)
      setPrepaidAmount(getCheckinsData.prepaidTotal)
      setPostpaidAmount(getCheckinsData.postpaidTotal)
      setOnlineAmount(getCheckinsData.onlineTotal)

      let inData = getCheckinsData.checkins.filter((item) => {
        return item.isCheckedOut === false
      })

      setInVehicles(inData.length)
      setOutVehicles(getCheckinsData.checkins.length - inData.length)
    } else if (errorGetCheckins) {
      setLoading(false)
      toast(errorGetCheckins, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getCheckinsData, errorGetCheckins])

  useEffect(() => {
    if (data) {
      setFilteredData(data)
    }
  }, [data])

  const headCells = [
    {
      field: 'vehicleNumber',
      title: 'Vehicle Number',
    },
    {
      field: 'mobile',
      title: 'Mobile',
    },
    {
      field: 'status',
      title: 'Status',
    },
    {
      field: 'checkinUser',
      title: 'Checkin User',
    },
    {
      field: 'checkoutUser',
      title: 'Checkout User',
    },
    {
      field: 'parking',
      title: 'Parking',
    },
    {
      field: 'inTime',
      title: 'In Time',
    },
    {
      field: 'outTime',
      title: 'Out Time',
    },
    {
      field: 'paymentStatus',
      title: 'Payment Status',
    },
    {
      field: 'prepaidAmount',
      title: 'Prepaid Amount',
    },
    {
      field: 'postpaidAmount',
      title: 'Postpaid Amount',
    },
    {
      field: 'onlineAmount',
      title: 'Online Amount',
    },
    {
      field: 'isFOC',
      title: 'FOC',
    },
    {
      field: 'recalledTime',
      title: 'Recalled Time',
    },
    {
      field: 'allocationTime',
      title: 'Allocation Time',
    },
    {
      field: 'arrivalTime',
      title: 'Arrival Time',
    },
    {
      field: 'recheckinTime',
      title: 'Recheckin Time',
    },
    {
      field: 'checkOutType',
      title: 'Check Out Type',
    },
    {
      field: 'manualCheckOutTime',
      title: 'Manual Check Out Time',
    },
  ]

  const searchCheckins = () => {
    dispatch(
      getCheckinsValet(page, status, from, to, parking, vehicleNumber, mobile)
    )
    // setState('')
    // setFrom('')
    // setTo('')
    // setParking('')
    // setVehicleNumber('')
    // setMobile('')
  }

  const allSelection = (e) => {
    if (e.target.checked) {
      const ids = []
      filteredData.forEach((item) => {
        ids.push(item._id)
      })
      setSelected(ids)
    } else {
      setSelected([])
    }
  }

  const selectSingle = (e, id) => {
    if (e.target.checked) {
      setSelected([...selected, id])
      // * Check if total rows selected is equal to total data
      if (selected.length === filteredData.length) {
        setSelected(filteredData)
      }
    } else {
      setSelected(selected.filter((item) => item !== id))
    }
  }

  // * Manual Checkouts
  const manualCheckOut = () => {
    dispatch(manualCheckoutsValet(selected))
  }

  const manualCheckoutInfo = useSelector((state) => state.manualCheckoutInfo)
  const { loadingManualCheckout, errorManualCheckout, manualCheckoutData } =
    manualCheckoutInfo

  useEffect(() => {
    dispatch({ type: MANUAL_CHECKOUT_RESET })
    if (manualCheckoutData) {
      toast(manualCheckoutData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setSelected([])
      setTimeout(() => {
        dispatch(getCheckinsValet(page))
      }, 1000)
    } else if (errorManualCheckout) {
      toast(errorManualCheckout, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [manualCheckoutData, errorManualCheckout])

  const focHandler = () => {
    dispatch(focValet(selected))
  }

  const focInfo = useSelector((state) => state.focInfo)
  const { loadingFoc, errorFoc, focData } = focInfo

  useEffect(() => {
    dispatch({ type: FOC_RESET })
    if (focData) {
      toast(focData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })
      setSelected([])
      setTimeout(() => {
        dispatch(getCheckinsValet(page))
      }, 1000)
    } else if (errorFoc) {
      toast(errorFoc, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [focData, errorFoc])

  const download = async () => {
    dispatch(downloadValet(status, from, to, parking, vehicleNumber, mobile))
  }

  const downloadInfo = useSelector((state) => state.downloadInfo)
  const { loadingDownload, downloadData, errorDownload } = downloadInfo

  useEffect(() => {
    dispatch({ type: DOWNLOAD_RESET })
    if (downloadData) {
      downloadExcel(downloadData)
    } else if (errorDownload) {
      toast(errorDownload, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [downloadData, errorDownload])

  const downloadExcel = async (downloadData) => {
    await writeXlsxFile(downloadData, {
      fileName: 'Checkins.xlsx',
    })
  }

  if (loading) {
    return <Loading />
  }

  return (
    <>
      <div className='w-full h-full'>
        <div className='flex flex-row justify-between'>
          <div className='flex flex-row gap-4'>
            <h1 className='text-2xl font-semibold'>Checkins</h1>
            {dateRange && (
              <p
                style={{
                  paddingTop: 6,
                  paddingBottom: 6,
                  fontSize: 16,
                }}
              >
                ({from} - {to})
              </p>
            )}
          </div>
          <Button
            custom='py-2 mt-4 md:mt-0 bg-green-500 hover:bg-green-700'
            type='button'
            onClick={download}
            text='Download'
            disabled={loadingDownload}
          />
          {selected.length > 0 && (
            <div className='flex flex-row gap-4'>
              <Button
                custom='py-2 mt-4 md:mt-0 bg-green-500 hover:bg-green-700'
                type='button'
                onClick={focHandler}
                text='FOC'
                disabled={loadingFoc}
              />
              <Button
                custom='py-2 mt-4 md:mt-0 bg-red-500 hover:bg-red-700'
                type='button'
                onClick={manualCheckOut}
                text='Check Out'
                disabled={loadingManualCheckout}
              />
            </div>
          )}
        </div>
        <div className='bg-white shadow-md rounded px-8 py-4 my-4'>
          <div className='flex flex-col md:flex-row justify-between'>
            <div className='flex flex-wrap md:flex-row gap-4 items-center'>
              <Select
                name='Status *'
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                options={[
                  { id: 'Inspection Pending', title: 'Inspection Pending' },
                  { id: 'Location Pending', title: 'Location Pending' },
                  { id: 'Checked In', title: 'Checked In' },
                  { id: 'Check Out Start', title: 'Check Out Start' },
                  { id: 'Recalled', title: 'Recalled' },
                  { id: 'Hold', title: 'Hold' },
                  { id: 'Checked Out', title: 'Checked Out' },
                ]}
              />
              <button
                className='py-1 px-10 border-2 border-gray-400 rounded text-md'
                type='button'
                onClick={() => setDateModal(true)}
              >
                Date Filter
              </button>
              <Select
                name='Parking *'
                value={parking}
                onChange={(e) => setParking(e.target.value)}
                options={parkingsFormatted}
              />
              <Input
                name='Vehicle Number *'
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
              />
              <Input
                name='Mobile *'
                type='number'
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </div>
            <Button
              custom='py-2 mt-4 md:mt-0 h-10'
              type='button'
              onClick={searchCheckins}
              text='Search'
            />
          </div>
        </div>
        <div className='flex flex-col md:flex-row gap-4 w-full my-4'>
          <div className='bg-white shadow-md rounded px-8 py-4 w-full md:w-1/2 flex flex-col gap-2'>
            <div className='flex flex-row justify-between'>
              <h3 className='text-md font-semibold'>Total Collections</h3>
              <h3 className='text-md font-semibold'>{totalAmount}</h3>
            </div>
            <div className='flex flex-row justify-between'>
              <p className='text-sm'>Prepaid</p>
              <p className='text-sm'>{prepaidAmount || 0}</p>
            </div>
            <div className='flex flex-row justify-between'>
              <p className='text-sm'>Postpaid</p>
              <p className='text-sm'>{postpaidAmount || 0}</p>
            </div>
            <div className='flex flex-row justify-between'>
              <p className='text-sm'>Online</p>
              <p className='text-sm'>{onlineAmount || 0}</p>
            </div>
          </div>
          <div className='bg-white shadow-md rounded px-8 py-4 w-full md:w-1/2 flex flex-col gap-2'>
            <div className='flex flex-row justify-between'>
              <h3 className='text-md font-semibold'>Total Vehicles</h3>
              <h3 className='text-md font-semibold'>{total}</h3>
            </div>
            <div className='flex flex-row justify-between'>
              <p className='text-sm'>In</p>
              <p className='text-sm'>{inVehicles || 0}</p>
            </div>
            <div className='flex flex-row justify-between'>
              <p className='text-sm'>Out</p>
              <p className='text-sm'>{outVehicles || 0}</p>
            </div>
          </div>
        </div>
        <div
          className='w-full bg-white mb-20'
          style={{ overflowX: 'auto', height: '85%' }}
        >
          <table
            className='w-full'
            style={{ height: '100%', position: 'relative' }}
          >
            <thead
              className='w-full bg-white'
              style={{ position: 'sticky', insetBlockStart: 0 }}
            >
              <tr className='w-full flex flex-row p-4 gap-2 items-center border border-b-1'>
                <th className='mr-5'>
                  <input
                    type='checkbox'
                    onChange={(e) => allSelection(e)}
                    checked={selected.length === filteredData.length}
                  />
                </th>
                {headCells.map((item) => (
                  <th
                    className='w-44 flex items-center'
                    key={item.field}
                    style={{ cursor: 'pointer' }}
                    onClick={() => sorting(item.field)}
                  >
                    <p className='text-sm font-semibold'>{item.title}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody style={{ overflowY: 'auto', height: '60%' }}>
              {filteredData.map((item, index) => (
                <tr
                  key={item.index}
                  className='w-full flex flex-row p-4 gap-2 items-center border border-b-1'
                >
                  <td className='mr-5'>
                    <input
                      type='checkbox'
                      onChange={(e) => selectSingle(e, item._id)}
                      checked={selected.includes(item._id)}
                    />
                  </td>
                  {headCells.map((head) => (
                    <>
                      {head.field === 'checkinUser' ||
                      head.field === 'parking' ||
                      head.field === 'checkoutUser' ? (
                        <td className='w-44 flex items-center'>
                          <p style={{ fontSize: 14 }}>
                            {item[head.field] ? item[head.field].name : 'N/A'}
                          </p>
                        </td>
                      ) : head.field === 'isFOC' ? (
                        <td className='w-44 flex items-center'>
                          <p style={{ fontSize: 14 }}>
                            {item[head.field] === true ? 'Yes' : 'No'}
                          </p>
                        </td>
                      ) : head.field === 'mobile' ? (
                        <>
                          {adminInfo.role === 'Facility' ? (
                            <td className='w-44 flex items-center'>
                              <p style={{ fontSize: 14 }}>
                                {item[head.field].showMobile === true
                                  ? item[head.field]
                                  : 'N/A'}
                              </p>
                            </td>
                          ) : (
                            <td className='w-44 flex items-center'>
                              <p style={{ fontSize: 14 }}>{item[head.field]}</p>
                            </td>
                          )}
                        </>
                      ) : (
                        <td className='w-44 flex items-center'>
                          <p style={{ fontSize: 14 }}>
                            {item[head.field] || 'N/A'}
                          </p>
                        </td>
                      )}
                    </>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div
            className='w-full bg-white border border-t-1 flex flex-row justify-between p-4'
            style={{ position: 'sticky', insetBlockEnd: 0 }}
          >
            <p className='text-sm'>
              Showing{' '}
              {page === 1
                ? '0 - ' + filteredData.length * page
                : filteredData.length * (page - 1) +
                  1 +
                  ' - ' +
                  filteredData.length * page}
              , Out of {total}
            </p>
            <div className='flex flex-row items-center gap-2'>
              <button
                className='border border-1 px-1 border-black text-sm'
                style={{ cursor: 'pointer' }}
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                &#171;
              </button>
              <button
                className='border border-1 px-1 border-black text-sm'
                style={{ cursor: 'pointer' }}
                onClick={() => setPage(page + 1)}
                disabled={page === pages}
              >
                &#187;
              </button>
            </div>
          </div>
        </div>
        {dateModal && (
          <>
            <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
              <div className='relative w-auto my-6 mx-auto max-w-full'>
                {/*content*/}
                <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                  {/*header*/}
                  <div className='flex items-center justify-between py-3 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                    <h3 className='text-lg font-semibold'>Date Range</h3>
                  </div>
                  {/*body*/}
                  <div className='relative p-6 flex-auto'>
                    <DateRangePicker
                      onChange={(item) => setState([item.selection])}
                      showSelectionPreview={true}
                      moveRangeOnFirstSelection={false}
                      months={2}
                      ranges={state}
                      direction='horizontal'
                    />
                  </div>
                  {/*footer*/}
                  <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                    <button
                      className='text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                      type='button'
                      onClick={() => setDateModal(false)}
                    >
                      Close
                    </button>
                    <button
                      className='bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                      type='button'
                      onClick={submitDateRange}
                    >
                      Okay
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
          </>
        )}
      </div>
    </>
  )
}

export default Checkins
